// import packages
import React, { useContext, useEffect, useState } from "react";
import { useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import { Button, Card, FormLayout, Layout, PageActions, TextField } from "@shopify/polaris";
import { ViewMinor, HideMinor } from "@shopify/polaris-icons";

// import hoc
import { withErrorBoundary } from "lib/hoc";

// import helpers and context
import { baseHelper, storageHelper } from "lib/helpers";
import { Banner } from "lib/components";
import { PublicContext } from "lib/context";

const STAFF_LOGIN = gql`
  mutation staffLogin($input: Credential) {
    staffLogin(input: $input) {
      data {
        token
      }
      status
      error
    }
  }
`;

const StaffLogin = () => {
  const { history } = useContext(PublicContext);
  const [staffLoginUser, { loading }] = useMutation(STAFF_LOGIN);

  const [values, setValues] = useState({ email: "", password: "" });
  const [errorMessage, setErrorMessage] = useState({});
  const [banner, setBanner] = useState({ isOpen: false, status: "", title: "" });
  const [submitEnabled, setSubmitEnabled] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const hasErrors = !!(errorMessage.email || errorMessage.password);
    setSubmitEnabled(values.email && values.password && !hasErrors);
  }, [values, errorMessage]);

  const handleChange = (key, value) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleValidation = (field, value) => {
    if (!value || !value.trim()) {
      setErrorMessage((prev) => ({ ...prev, [field]: `${field} is required` }));
    } else {
      setErrorMessage((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const onSubmit = async () => {
    handleValidation("email", values.email);
    handleValidation("password", values.password);
    if (!submitEnabled) return;

    staffLoginUser({ variables: { input: { email: values.email, password: values.password } } })
      .then((response) => {
        const loginResponse = baseHelper.getResponseData(response.data, "staffLogin");
        const loginError = baseHelper.getResponseError(response.data, "staffLogin");
        if (loginError) {
          setBanner({ isOpen: true, status: "critical", title: loginError });
        } else if (loginResponse && loginResponse.token) {
          [
            "buttonCSS",
            "headerColor",
            "loginText",
            "logoUrl",
            "register",
            "brandName",
            "headerFont",
            "hideMcDetails",
          ].forEach((key) => storageHelper.remove(key));
          storageHelper.set("token", loginResponse.token);
          storageHelper.set("isInitial", true);
          storageHelper.set("email", values.email);
          history.push("/");
        }
      })
      .catch(() => {
        setBanner({ isOpen: true, status: "critical", title: "An unexpected error occurred. Please try again." });
      });
  };

  const renderViewIcon = () => (
    <Button id="eyeButton" plain icon={visible ? HideMinor : ViewMinor} onClick={() => setVisible(!visible)} />
  );

  return (
    <>
      <Banner
        isOpen={banner.isOpen}
        status={banner.status}
        title={banner.title}
        onDismiss={() => setBanner({ isOpen: false, status: "", title: "" })}
      />
      <br />
      <Layout>
        <Layout.AnnotatedSection title="Staff Login" description="Sign in with your staff account credentials.">
          <Card sectioned>
            <FormLayout>
              <TextField
                id="email"
                value={values.email || ""}
                onChange={(value) => handleChange("email", value)}
                onBlur={() => handleValidation("email", values.email)}
                label="Email*"
                name="email"
                placeholder="Enter your email address"
                error={errorMessage.email}
              />
              <TextField
                id="password"
                value={values.password || ""}
                onChange={(value) => handleChange("password", value)}
                onBlur={() => handleValidation("password", values.password)}
                label="Password*"
                type={visible ? "text" : "password"}
                name="password"
                placeholder="Enter your password"
                error={errorMessage.password}
                suffix={renderViewIcon()}
              />
            </FormLayout>
          </Card>
          <PageActions
            primaryAction={{
              id: "staffLoginButton",
              content: "Sign In",
              onAction: onSubmit,
              disabled: !submitEnabled,
              loading,
            }}
          />
        </Layout.AnnotatedSection>
      </Layout>
    </>
  );
};

export default withErrorBoundary(StaffLogin);
